import { AdditionalTypeTag, Tag } from 'Atoms';
import { HStack, MenuButton, useDisclosure } from '@chakra-ui/react';
import { HelpTooltip } from 'Molecules/HelpTooltip';
import { Menu } from 'Molecules/Menu';
import { useMemo, useState } from 'react';
import { Typography } from 'Tokens';
import { LearnMoreDrawer } from 'Molecules/LearnMoreDrawer';

interface Props {
  tags: string[];
  isHStack?: boolean;
  maxWidthInPixels?: number;
}

const Wrapper = ({ isHStack, children }: { isHStack: boolean; children: React.ReactNode }) => {
  if (isHStack) {
    return <HStack spacing="0px">{children}</HStack>;
  }
  return <span>{children}</span>;
};

export const MetricTagsList = ({ tags, isHStack = true, maxWidthInPixels }: Props) => {
  return (
    <Wrapper isHStack={isHStack}>
      {tags.slice(0, 2).map((tag, index) => (
        <Tag
          key={index}
          title={tag}
          mr="4px"
          p="2px 6px 2px 4px"
          w="max-content"
          mt="2px"
          truncatable={true}
          maxWidthInPixels={maxWidthInPixels}
        />
      ))}
      {tags?.length > 2 && (
        <Menu
          sections={[{ actions: tags.slice(2).map((tag) => ({ id: tag, title: tag })), title: '' }]}
          customMenuButton={
            <MenuButton
              as={Tag}
              clickable={true}
              key={2}
              title={`+${tags.length - 2}`}
              mt="2px"
              mr="4px"
              p="2px 6px 2px 4px"
              w="max-content"
            />
          }
        />
      )}
    </Wrapper>
  );
};

export const AdditionalTypesGroup = ({
  tagsWithHelp,
}: {
  tagsWithHelp: { reference: string; title: string; description: string; helpText: string }[];
}) => {
  const firstTag = useMemo(() => tagsWithHelp?.[0], [tagsWithHelp]);
  const [learnMoreText, setLearnMoreText] = useState('');
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  if (!tagsWithHelp.length) return <Typography>—</Typography>;
  return (
    <HStack w="fit-content" lineHeight="sm" spacing="4px">
      <AdditionalTypeTag
        key={1}
        label={firstTag.title}
        description={firstTag.description}
        onHelpClick={() => {
          setLearnMoreText(firstTag.helpText);
          if (firstTag.helpText.length) onDrawerOpen();
        }}
      />
      {tagsWithHelp.length > 1 && (
        <Menu
          sections={[
            {
              actions: tagsWithHelp.slice(1).map((tag) => ({
                id: tag.reference,
                title: tag.title ?? tag.reference,
                onClick: () => {
                  setLearnMoreText(tag.helpText);
                  if (tag.helpText.length) onDrawerOpen();
                },
                rightElement: <HelpTooltip label={tag.description} />,
              })),
            },
          ]}
          customMenuButton={
            <MenuButton
              as={Tag}
              clickable={true}
              key={2}
              px="10px"
              py="4px"
              h="28px"
              borderRadius="4px"
            >
              <Typography variant="bodyStrong" w="fit-content">
                +{tagsWithHelp.length - 1}
              </Typography>
            </MenuButton>
          }
        />
      )}
      <LearnMoreDrawer isOpen={isDrawerOpen} onClose={onDrawerClose} description={learnMoreText} />
    </HStack>
  );
};
